// Per-conversation E2E trust state (persisted in localStorage). Closes two
// hostile-server gaps that the crypto primitives alone do not:
//
//  (1) SECURED flag — once a p2p chat has ever been E2E-encrypted, we must refuse
//      to SILENTLY downgrade it to plaintext. A malicious/compromised server that
//      strips the peer's published key would otherwise turn encryption off with no
//      warning. Callers block the send when a secured chat can no longer encrypt.
//
//  (2) First-seen peer identity key (idDH) — detect a key change (possible MITM /
//      server key-swap). The safety number is otherwise display-only and never
//      compared, so an identity swap would be accepted with a silent re-handshake.
//
// Keys are namespaced by the local user's uid so multiple accounts on one browser
// don't cross-contaminate. Best-effort: if localStorage is unavailable we fail
// OPEN for (1) (never block) rather than break messaging.

const SECURED = 'nx.e2e.secured.'; // + myUid + '.' + topic -> '1'
const IDDH = 'nx.e2e.iddh.';       // + myUid + '.' + topic -> base64(idDH)

function ls() {
  try { return window.localStorage; } catch (e) { return null; }
}
function b64(u8) {
  return btoa(String.fromCharCode.apply(null, u8));
}

// Mark a p2p chat as having been successfully E2E-encrypted (sent or received).
export function markSecured(myUid, topic) {
  const s = ls();
  if (s && myUid && topic) {
    try { s.setItem(SECURED + myUid + '.' + topic, '1'); } catch (e) { /* quota */ }
  }
}

// True if this chat was ever secured — i.e. plaintext here is now a downgrade.
export function isSecured(myUid, topic) {
  const s = ls();
  return !!(s && myUid && topic && s.getItem(SECURED + myUid + '.' + topic));
}

// Compare the peer's identity key to the first one we saw. Returns:
//   'first'   — no prior key on record (records it now),
//   'same'    — unchanged,
//   'changed' — DIFFERENT from the recorded key (possible MITM).
// idDH is a Uint8Array (the peer's X25519 identity public key).
export function checkIdentity(myUid, topic, idDH) {
  const s = ls();
  if (!s || !myUid || !topic || !idDH || !idDH.length) {
    return 'same';
  }
  const key = IDDH + myUid + '.' + topic;
  const cur = b64(idDH);
  let prev = null;
  try { prev = s.getItem(key); } catch (e) { /* ignore */ }
  if (!prev) {
    try { s.setItem(key, cur); } catch (e) { /* ignore */ }
    return 'first';
  }
  return prev === cur ? 'same' : 'changed';
}

// Record a (new) identity key as trusted — call ONLY after the user has re-verified
// the peer's new security key out-of-band. This is what unblocks sending to a changed
// key, so it must never be called automatically on a detected change.
export function acceptIdentity(myUid, topic, idDH) {
  const s = ls();
  if (s && myUid && topic && idDH && idDH.length) {
    try { s.setItem(IDDH + myUid + '.' + topic, b64(idDH)); } catch (e) { /* ignore */ }
  }
}

// A short, human-comparable fingerprint of an identity key, so the user can verify a
// changed key out-of-band (read it to the peer over a trusted channel). 16 hex chars
// of the key in groups of four — not the full safety number, enough to catch a swap.
export function identityFingerprint(idDH) {
  if (!idDH || !idDH.length) {
    return '';
  }
  const hex = Array.from(idDH).map((x) => x.toString(16).padStart(2, '0')).join('');
  return (hex.slice(0, 16).match(/.{1,4}/g) || []).join(' ').toUpperCase();
}
